import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { StaffRole, StaffUser } from '@gisgo/shared';

import { AuthApi, ApiMeUser } from './auth-api';

const TOKEN_KEY = 'gisgo-ops-token';

interface AuthState {
  readonly user: StaffUser | null;
  readonly pending: boolean;
  readonly error: string | null;
}

const initialState: AuthState = { user: null, pending: false, error: null };

/** Rôle staff le plus fort porté par le compte (un user peut en cumuler). */
function staffRoleOf(user: ApiMeUser): StaffRole | null {
  if (user.roles.includes('admin')) {
    return 'admin';
  }
  if (user.roles.includes('dispatcher')) {
    return 'dispatcher';
  }
  if (user.roles.includes('sales')) {
    return 'sales';
  }
  return null;
}

/**
 * Auth Sanctum réelle : login → token (localStorage, partagé avec l'API et
 * Reverb) → hydratation GET /auth/me au boot (provideAppInitializer).
 * Un compte sans rôle staff est rejeté (outil interne).
 */
export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ user }) => ({
    role: computed<StaffRole | null>(() => user()?.role ?? null),
    initials: computed(() => {
      const name = user()?.name ?? '';
      return name
        .split(/\s+/)
        .map((part) => part[0] ?? '')
        .join('')
        .slice(0, 2)
        .toUpperCase();
    }),
  })),
  withMethods((store) => {
    const api = inject(AuthApi);
    const router = inject(Router);

    const applyMe = (me: ApiMeUser): boolean => {
      const role = staffRoleOf(me);
      if (role === null) {
        window.localStorage.removeItem(TOKEN_KEY);
        patchState(store, {
          user: null,
          error: 'Ce compte n’a pas de rôle staff (dispatcher, sales ou admin).',
        });
        return false;
      }
      patchState(store, {
        user: { id: me.id, name: me.name, email: me.email ?? '', role },
        error: null,
      });
      return true;
    };

    return {
      /** Hydratation au boot (initializer) — silencieuse si pas de token. */
      async hydrate(): Promise<void> {
        if (window.localStorage.getItem(TOKEN_KEY) === null) {
          return;
        }
        try {
          applyMe(await firstValueFrom(api.me()));
        } catch {
          window.localStorage.removeItem(TOKEN_KEY);
          patchState(store, { user: null });
        }
      },

      login(email: string, password: string): void {
        patchState(store, { pending: true, error: null });
        api.login(email, password).subscribe({
          next: (token) => {
            window.localStorage.setItem(TOKEN_KEY, token);
            api.me().subscribe({
              next: (me) => {
                patchState(store, { pending: false });
                if (applyMe(me)) {
                  void router.navigateByUrl('/');
                }
              },
              error: () => patchState(store, { pending: false, error: 'Connexion impossible.' }),
            });
          },
          error: () => patchState(store, { pending: false, error: 'Identifiants invalides.' }),
        });
      },

      logout(): void {
        window.localStorage.removeItem(TOKEN_KEY);
        patchState(store, { user: null, error: null });
        void router.navigateByUrl('/login');
      },

      /** Outil de dev : simule un rôle pour vérifier les gates RBAC. */
      switchRole(role: StaffRole): void {
        const user = store.user();
        if (user) {
          patchState(store, { user: { ...user, role } });
        }
      },
    };
  }),
);
